import { classifyMeld, deadwoodValue, isWild, type CardId, type Group, type MeldContext } from '@rummy/engine';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useI18n } from '../i18n/index.tsx';
import { haptic } from '../feedback/haptics.ts';
import {
  insertCards,
  layoutTray,
  moveCards,
  resolveDropTarget,
  solveRows,
  withoutCards,
  type DropTarget,
  type TrayAlignment,
  type TrayGeometry,
  type TrayLayout,
} from '../game/trayLayout.ts';
import { PlayingCard } from './PlayingCard.tsx';

const LONG_PRESS_MS = 360;
/** Reveal the face before the long-press becomes a multi-select gesture. */
const INSPECT_PRESS_MS = 240;
const DRAG_THRESHOLD_PX = 4;
/** px per ms, upward. A deliberate flick, not a fumble. */
const FLICK_VELOCITY = -0.55;

export interface TrayProps {
  groups: Group[];
  ctx: MeldContext;
  onChange: (groups: Group[]) => void;
  onDiscard: (card: CardId) => void;
  canDiscard: boolean;
  drawn: CardId | null;
  /** Live bounding box of the discard pile, for drag-to-discard. */
  getDiscardRect: () => DOMRect | null;
  /** Lets the table expose an explicit, thumb-friendly discard action. */
  onSelectionChange?: (cards: CardId[]) => void;
  /** A focused flow, such as choosing a finish card, can require one card. */
  selectionLimit?: number;
  /** During a discard turn, a normal tap replaces the selected card. A
   * long-press still keeps multi-card selection available for rearranging. */
  singleTapSelection?: boolean;
  /** Lets a parent clear the tray's private selection when the surrounding
   * flow changes (for example, from discard to declaration). */
  selectionResetKey?: number;
  /** A lane just became a valid meld after a player rearranged it. */
  onMeldComplete?: () => void;
  disabled?: boolean;
  /** Ceiling on card height, from the space the table can spare. */
  maxCardHeight?: number;
  /** Phones have a gentle leading bias; desktop hands remain centred. */
  alignment?: TrayAlignment;
  /** Card-height ceiling for a hand laid out on two rows; omitted, the hand
   *  always uses one. Only a portrait phone has the height to spare. */
  twoRowMaxCardHeight?: number | null;
}

interface DragState {
  pointerId: number;
  cards: CardId[];
  startX: number;
  startY: number;
  x: number;
  y: number;
  grabX: number;
  originLeft: number;
  originTop: number;
  /** The grabbed card's row offset and the cards' inset from the tray top. */
  grabSlotY: number;
  rowTop: number;
  moved: boolean;
  lastY: number;
  lastT: number;
  velocityY: number;
}

/**
 * The tray (§6.3) — "this is where web rummy implementations fail and where the
 * product is won".
 *
 * Groups are lanes with visible gaps. A card drags between lanes; dropping it
 * in a gap makes a new lane, which is the entire mechanism for forming a group
 * — no button to find, no modal. Long-press first shows a readable card face,
 * then starts a multi-selection; the selection drags as one. A flick upward,
 * a drag onto the pile, the explicit Discard action, or a desktop double-click
 * all throw a card. It is pointer events throughout, so mouse, trackpad, pen
 * and touch are one code path rather than three.
 */
export function Tray({
  groups,
  ctx,
  onChange,
  onDiscard,
  canDiscard,
  drawn,
  getDiscardRect,
  onSelectionChange,
  selectionLimit,
  singleTapSelection = false,
  selectionResetKey = 0,
  onMeldComplete,
  disabled = false,
  maxCardHeight,
  alignment = 'center',
  twoRowMaxCardHeight = null,
}: TrayProps) {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(960);
  const [selection, setSelection] = useState<CardId[]>([]);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [target, setTarget] = useState<DropTarget | null>(null);
  const [overDiscard, setOverDiscard] = useState(false);
  /** The lane that just became a valid combination, for the length of its snap. */
  const [snapLane, setSnapLane] = useState<number | null>(null);
  // Pointer events can arrive faster than React commits state. These refs make
  // the current drag position and drop target available to the matching
  // pointer-up event even if it lands in the same render frame.
  const dragRef = useRef<DragState | null>(null);
  const targetRef = useRef<DropTarget | null>(null);
  const overDiscardRef = useRef(false);
  const [cursor, setCursor] = useState(0);
  const [held, setHeld] = useState<CardId[] | null>(null);
  const [inspection, setInspection] = useState<{ id: CardId; x: number } | null>(null);
  const longPress = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inspectPress = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressActivated = useRef(false);
  const lastDesktopClick = useRef<{ id: CardId; at: number } | null>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  const flat = useMemo(() => groups.flat(), [groups]);

  /**
   * Sized from the committed grouping, not the drag preview, so cards do not
   * resize under the finger while a new lane is opening.
   */
  const { geom, split } = useMemo<{ geom: TrayGeometry; split: number | null }>(
    () => solveRows(width, groups, maxCardHeight ?? Infinity, twoRowMaxCardHeight),
    [width, groups, maxCardHeight, twoRowMaxCardHeight],
  );

  /**
   * The grouping as it would be if the drag ended right now. The dragged cards
   * keep a reserved slot, so the rest of the tray opens up under the finger
   * instead of snapping into place at the end.
   */
  const preview = useMemo(() => {
    if (!drag?.moved || target === null) return groups;
    return insertCards(withoutCards(groups, drag.cards), drag.cards, target);
  }, [groups, drag, target]);

  const layout = useMemo(
    () => layoutTray(preview, geom, alignment, split),
    [preview, geom, alignment, split],
  );

  const meldKinds = useMemo(
    () =>
      preview.map((group) =>
        group.length >= ctx.config.minMeldSize ? classifyMeld(group, ctx) : null,
      ),
    [preview, ctx],
  );

  const commit = useCallback(
    (next: Group[]) => {
      const clean = next.filter((group) => group.length > 0);
      const before = new Set(validMeldSignatures(groups, ctx));
      /*
       * Completing a combination is the payoff of the whole game and it used to
       * be a line appearing under some cards. Finding *which* lane became valid
       * — not merely that one did — is what lets it close up and settle.
       */
      const snapped = clean.findIndex(
        (group) =>
          group.length >= ctx.config.minMeldSize &&
          classifyMeld(group, ctx) !== null &&
          !before.has(group.join('|')),
      );
      if (snapped >= 0) {
        onMeldComplete?.();
        setSnapLane(snapped);
      }
      onChange(clean);
    },
    [groups, ctx, onChange, onMeldComplete],
  );

  /* Cleared on a timer rather than on animation end: a lane can be rearranged
     again mid-animation, and a stale class would suppress the next snap. */
  useEffect(() => {
    if (snapLane === null) return;
    const id = setTimeout(() => setSnapLane(null), 460);
    return () => clearTimeout(id);
  }, [snapLane]);

  const clearPressTimers = useCallback(() => {
    if (longPress.current) {
      clearTimeout(longPress.current);
      longPress.current = null;
    }
    if (inspectPress.current) {
      clearTimeout(inspectPress.current);
      inspectPress.current = null;
    }
  }, []);

  useEffect(() => {
    clearPressTimers();
    dragRef.current = null;
    targetRef.current = null;
    overDiscardRef.current = false;
    setSelection([]);
    setHeld(null);
    setInspection(null);
  }, [selectionResetKey, clearPressTimers]);

  useEffect(() => {
    onSelectionChange?.(selection);
  }, [onSelectionChange, selection]);

  useEffect(() => () => clearPressTimers(), [clearPressTimers]);

  // ------------------------------------------------------------------- drag

  const beginDrag = useCallback(
    (event: React.PointerEvent, id: CardId) => {
      if (disabled) return;
      // A secondary mouse button should keep its normal browser behaviour.
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const cardRect = event.currentTarget.getBoundingClientRect();
      const slot = layout.slots.get(id);
      const cards = selection.includes(id) ? orderSelection(groups, selection) : [id];

      // The tray deliberately owns this gesture. Besides stopping accidental
      // text selection, this prevents a touch hold from becoming a browser
      // gesture before it can become a card drag.
      event.preventDefault();
      container.setPointerCapture(event.pointerId);
      const nextDrag: DragState = {
        pointerId: event.pointerId,
        cards,
        startX: event.clientX,
        startY: event.clientY,
        x: event.clientX,
        y: event.clientY,
        // Use the live card edge horizontally, where its slot can vary. On
        // the vertical axis the card already has a CSS `top` inset, so the
        // drag transform starts from the card's row offset and only adds
        // pointer delta. Including cardRect.top would apply that inset twice.
        grabX: event.clientX - cardRect.left,
        originLeft: rect.left,
        originTop: rect.top,
        grabSlotY: slot?.y ?? 0,
        rowTop: cardRect.top - rect.top - (slot?.y ?? 0),
        moved: false,
        lastY: event.clientY,
        lastT: event.timeStamp,
        velocityY: 0,
      };
      dragRef.current = nextDrag;
      targetRef.current = null;
      overDiscardRef.current = false;
      setDrag(nextDrag);
      setTarget(null);
      setOverDiscard(false);

      longPressActivated.current = false;
      inspectPress.current = setTimeout(() => {
        // The preview is centred over the actual card, rather than the finger,
        // so the thumb never hides the rank the player came to inspect.
        const x = slot ? slot.x + geom.cardWidth / 2 : geom.cardWidth / 2;
        setInspection({ id, x: Math.max(58, Math.min(width - 58, x)) });
      }, INSPECT_PRESS_MS);
      longPress.current = setTimeout(() => {
        longPressActivated.current = true;
        setSelection((prev) =>
          selectionLimit === 1 ? [id] : prev.includes(id) ? prev : [...prev, id],
        );
      }, LONG_PRESS_MS);
    },
    [disabled, layout, selection, groups, geom, width, selectionLimit],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const currentDrag = dragRef.current;
      if (!currentDrag || currentDrag.pointerId !== event.pointerId) return;
      const dx = event.clientX - currentDrag.startX;
      const dy = event.clientY - currentDrag.startY;
      const moved = currentDrag.moved || Math.hypot(dx, dy) > DRAG_THRESHOLD_PX;
      if (moved) {
        clearPressTimers();
        setInspection(null);
      }
      const dt = Math.max(1, event.timeStamp - currentDrag.lastT);
      const next: DragState = {
        ...currentDrag,
        x: event.clientX,
        y: event.clientY,
        moved,
        velocityY: (event.clientY - currentDrag.lastY) / dt,
        lastY: event.clientY,
        lastT: event.timeStamp,
      };
      dragRef.current = next;
      setDrag(next);
      if (!moved) return;

      const pile = canDiscard ? getDiscardRect() : null;
      const onPile =
        pile !== null &&
        event.clientX >= pile.left - 28 &&
        event.clientX <= pile.right + 28 &&
        event.clientY >= pile.top - 28 &&
        event.clientY <= pile.bottom + 28;

      if (onPile !== overDiscardRef.current) {
        overDiscardRef.current = onPile;
        setOverDiscard(onPile);
        haptic('snap');
      }
      if (onPile) {
        if (targetRef.current !== null) {
          targetRef.current = null;
          setTarget(null);
        }
        return;
      }

      const rest = withoutCards(groups, currentDrag.cards);
      const restLayout = layoutTray(rest, geom, alignment, split);
      const localX = event.clientX - next.originLeft - next.grabX + geom.cardWidth / 2;
      const localY = event.clientY - next.originTop - next.rowTop;
      const resolved = resolveDropTarget(localX, restLayout, rest, geom, localY);
      if (!sameTarget(resolved, targetRef.current)) {
        targetRef.current = resolved;
        setTarget(resolved);
        haptic('snap');
      }
    },
    [canDiscard, getDiscardRect, groups, geom, alignment, split, clearPressTimers],
  );

  const endDrag = useCallback(
    (event: React.PointerEvent) => {
      clearPressTimers();
      setInspection(null);
      const current = dragRef.current;
      const finalTarget = targetRef.current;
      const finalOverDiscard = overDiscardRef.current;
      dragRef.current = null;
      targetRef.current = null;
      overDiscardRef.current = false;
      setDrag(null);
      setTarget(null);
      setOverDiscard(false);
      if (!current || current.pointerId !== event.pointerId) return;

      if (!current.moved) {
        const id = current.cards[0]!;
        if (longPressActivated.current) {
          longPressActivated.current = false;
          return;
        }
        // Keep the established desktop shortcut, but do not make the same
        // invisible gesture do a destructive action on a touch device.
        if (event.pointerType === 'mouse') {
          const previous = lastDesktopClick.current;
          if (previous && previous.id === id && event.timeStamp - previous.at < 350) {
            lastDesktopClick.current = null;
            if (canDiscard) {
              onDiscard(id);
              setSelection([]);
              return;
            }
          }
          lastDesktopClick.current = { id, at: event.timeStamp };
        }
        // A tap selects a card; the table then presents a real discard button.
        // Double-tap used to throw a card with no visible affordance, which is
        // both hard to discover and too easy to trigger accidentally.
        setSelection((prev) => {
          if (prev.includes(id)) return prev.filter((c) => c !== id);
          return selectionLimit === 1 || singleTapSelection ? [id] : [...prev, id];
        });
        setCursor(Math.max(0, flat.indexOf(id)));
        return;
      }

      // A flick upward off the top of the tray discards — the fastest way to
      // throw a card once the hand is arranged.
      const flicked =
        current.velocityY < FLICK_VELOCITY &&
        current.y < current.originTop + geom.cardHeight * 0.45;

      if (canDiscard && current.cards.length === 1 && (finalOverDiscard || flicked)) {
        onDiscard(current.cards[0]!);
        setSelection([]);
        return;
      }

      if (finalTarget !== null) commit(moveCards(groups, current.cards, finalTarget));
      setSelection([]);
    },
    [groups, commit, canDiscard, geom, flat, onDiscard, clearPressTimers, selectionLimit, singleTapSelection],
  );

  // --------------------------------------------------------------- keyboard

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return;
      const cards = preview.flat();
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowLeft': {
          event.preventDefault();
          const delta = event.key === 'ArrowRight' ? 1 : -1;
          setCursor((c) => Math.max(0, Math.min(cards.length - 1, c + delta)));
          return;
        }
        case ' ': {
          event.preventDefault();
          const id = cards[cursor];
          if (id) setHeld((h) => (h ? null : [id]));
          return;
        }
        case 'Enter': {
          event.preventDefault();
          if (!held) return;
          const { lane, index } = locate(preview, cards[cursor] ?? '');
          commit(moveCards(groups, held, { kind: 'into', lane, index }));
          setHeld(null);
          return;
        }
        case 'ArrowUp': {
          if (!canDiscard) return;
          event.preventDefault();
          const id = cards[cursor];
          if (id) onDiscard(id);
          return;
        }
        case 'ArrowDown': {
          if (!held) return;
          event.preventDefault();
          const { lane } = locate(preview, cards[cursor] ?? '');
          commit(moveCards(groups, held, { kind: 'new-lane', before: lane + 1 }));
          setHeld(null);
          return;
        }
        default:
          return;
      }
    },
    [disabled, preview, cursor, held, groups, commit, canDiscard, onDiscard],
  );

  useEffect(() => {
    if (flat.length > 0 && cursor >= flat.length) setCursor(flat.length - 1);
  }, [cursor, flat.length]);

  useEffect(() => {
    // Drop a selection that refers to cards no longer in the hand.
    setSelection((prev) => {
      const kept = prev.filter((id) => flat.includes(id));
      return kept.length === prev.length ? prev : kept;
    });
  }, [flat]);

  const cursorId = preview.flat()[cursor];

  return (
    <div className={`tray-shell${inspection ? ' is-inspecting' : ''}`}>
      <div
        ref={containerRef}
        className={[
          'tray',
          // The hand is intentionally a straight horizontal rack. It is a
          // comparison tool, not a fanned hand of physical cards.
          'tray--flat',
          drag?.moved ? 'is-dragging' : '',
          overDiscard ? 'is-over-pile' : '',
          disabled ? 'is-disabled' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={
          {
            '--cw': `${geom.cardWidth}px`,
            '--ch': `${geom.cardHeight}px`,
            // Height the second row adds; the tray's own height rules add it on.
            '--tray-extra': `${(layout.rows - 1) * layout.rowHeight}px`,
          } as React.CSSProperties
        }
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="application"
        aria-label={t('a11y.hand', { count: flat.length })}
        aria-describedby="tray-keyboard-help"
        /*
         * §10 — full layout mirroring, with one exception: card order and the
         * discard pile never mirror. Playing cards are a left-to-right
         * convention worldwide, and mirroring them is a bug that looks like a
         * feature. The tray's positions are computed in logical order and
         * anchored left, so this attribute is what keeps them on screen.
         */
        dir="ltr"
      >
        {/* Lane underlines: solid for a pure sequence, dotted for any other
            valid combination, nothing at all otherwise. A status indicator, not
            validation — no red, no error states, no scolding (§6.3). */}
        {layout.lanes.map((box, i) => {
          const meld = meldKinds[i] ?? null;
          const kind =
            meld?.kind === 'pure-sequence' ? 'pure' : meld === null ? 'none' : 'valid';
          /*
           * A group that is not a combination yet says what it costs if the
           * hand ends now. Labelling only the finished groups left a player
           * to work out for themselves which of the rest was the expensive
           * one. A number, not a verdict: neutral ink, no red (§6.3). A lane
           * holding more than half the hand is the unsorted remainder — the
           * whole hand, with the card just drawn beside it — not a group
           * anyone made, so it says nothing.
           */
          const lane = preview[i] ?? [];
          const loose =
            meld === null && layout.lanes.length > 1 && lane.length <= ctx.config.handSize / 2
              ? lane.reduce((sum, id) => sum + deadwoodValue(id, ctx), 0)
              : 0;
          return (
            <div
              key={`lane-${i}`}
              className={[
                `lane lane--${kind}`,
                target?.kind === 'into' && target.lane === i ? 'is-drop-target' : '',
                snapLane === i ? 'is-snapping' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                transform: `translate3d(${box.start}px, ${box.y}px, 0)`,
                width: `${box.end - box.start}px`,
              }}
            >
              {/* The word already carries the meaning without relying on
                  color (§6.3's "no red, no error states" cuts the other way
                  too: nothing here is color-only). The glyph is a second,
                  purely visual cue — legible at a glance, before the word is
                  even read. */}
              {meld !== null ? (
                <span className="lane__label">
                  {meld.kind === 'pure-sequence'
                    ? `┃ ${t('group.pure')}`
                    : meld.kind === 'impure-sequence'
                      ? `┆ ${t('group.impure')}`
                      : `◆ ${t('group.set')}`}
                </span>
              ) : (
                loose > 0 && (
                  <span className="lane__label lane__label--loose">{t('points.short', { points: loose })}</span>
                )
              )}
            </div>
          );
        })}

        {/* Drag a divider to move cards across a group boundary (§6.3). */}
        {!drag &&
          layout.lanes.slice(0, -1).map((box, i) =>
            // Lanes on different rows do not touch, so there is no boundary
            // between them to drag.
            layout.lanes[i + 1]!.row !== box.row ? null : (
              <LaneDivider
                key={`divider-${i}`}
                x={box.end + geom.laneGap / 2}
                y={box.y}
                onShift={(delta) => commit(shiftAcross(groups, i, delta))}
              />
            ),
          )}

        {preview.flat().map((id) => {
          const slot = layout.slots.get(id);
          if (!slot) return null;
          const dragged = drag?.moved === true && drag.cards.includes(id);
          const stackIndex = dragged ? drag!.cards.indexOf(id) : 0;
          const x = dragged
            ? drag!.x - drag!.originLeft - drag!.grabX + stackIndex * 18
            : slot.x;
          const y = dragged ? drag!.grabSlotY + drag!.y - drag!.startY + stackIndex * 5 : slot.y;
          return (
            <button
              key={id}
              type="button"
              className={[
                'tray__card',
                dragged ? 'is-dragging' : '',
                selection.includes(id) ? 'is-selected' : '',
                held?.includes(id) ? 'is-held-kb' : '',
                cursorId === id ? 'is-cursor' : '',
                snapLane === slot.lane && !dragged ? 'is-snapping' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                transform: `translate3d(${x}px, ${y}px, 0)`,
                zIndex: dragged ? 900 + stackIndex : slot.lane * 20 + slot.index,
                // Staggered from the left of the group, so the snap reads as
                // the cards closing against each other rather than pulsing.
                ['--snap-i' as string]: slot.index,
              }}
              onPointerDown={(e) => beginDrag(e, id)}
              aria-pressed={selection.includes(id)}
              tabIndex={-1}
            >
              <PlayingCard
                id={id}
                wild={isWild(id, ctx)}
                selected={selection.includes(id)}
                dragging={dragged}
                held={drawn === id}
              />
            </button>
          );
        })}

        {drag?.moved && target?.kind === 'new-lane' && (
          <div
            className="tray__new-lane"
            style={{ transform: newLaneTransform(layout, target.before, geom) }}
          >
            <span>{t('group.new')}</span>
          </div>
        )}
      </div>

      <p id="tray-keyboard-help" className="visually-hidden">
        {t('a11y.keyboard')}
      </p>

      {inspection && (
        <div
          className="tray__peek"
          style={{ insetInlineStart: `${inspection.x}px` }}
          role="status"
          aria-live="polite"
        >
          <PlayingCard id={inspection.id} wild={isWild(inspection.id, ctx)} />
        </div>
      )}

      {selection.length > 0 && (
        <div className="tray__selection" role="status">
          {t('group.selected', { count: selection.length })}
        </div>
      )}

    </div>
  );
}

function LaneDivider({ x, y, onShift }: { x: number; y: number; onShift: (delta: number) => void }) {
  const start = useRef<number | null>(null);
  const shifted = useRef(0);
  return (
    <div
      className="lane-divider"
      style={{ transform: `translate3d(${x}px, ${y}px, 0)` }}
      onPointerDown={(e) => {
        e.stopPropagation();
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        start.current = e.clientX;
        shifted.current = 0;
      }}
      onPointerMove={(e) => {
        if (start.current === null) return;
        e.stopPropagation();
        const steps = Math.round((e.clientX - start.current) / 34);
        if (steps !== shifted.current) {
          onShift(steps - shifted.current);
          shifted.current = steps;
        }
      }}
      onPointerUp={(e) => {
        e.stopPropagation();
        start.current = null;
      }}
      aria-hidden="true"
    />
  );
}

function sameTarget(a: DropTarget | null, b: DropTarget | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'new-lane' && b.kind === 'new-lane') return a.before === b.before;
  if (a.kind === 'into' && b.kind === 'into') return a.lane === b.lane && a.index === b.index;
  return false;
}

function orderSelection(groups: readonly Group[], selection: readonly CardId[]): CardId[] {
  const order = groups.flat();
  return [...selection].sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

function locate(groups: readonly Group[], id: CardId): { lane: number; index: number } {
  for (let lane = 0; lane < groups.length; lane++) {
    const index = groups[lane]!.indexOf(id);
    if (index >= 0) return { lane, index };
  }
  return { lane: Math.max(0, groups.length - 1), index: 0 };
}

/** Moves `delta` cards across the boundary between lane `i` and lane `i + 1`. */
function shiftAcross(groups: readonly Group[], i: number, delta: number): Group[] {
  const next = groups.map((g) => [...g]);
  const left = next[i];
  const right = next[i + 1];
  if (!left || !right) return next;
  for (let n = 0; n < Math.abs(delta); n++) {
    if (delta > 0 && right.length > 0) left.push(right.shift()!);
    else if (delta < 0 && left.length > 0) right.unshift(left.pop()!);
  }
  return next.filter((g) => g.length > 0);
}

/** Where the "new group" marker sits: in the gap the new lane will open. */
function newLaneTransform(layout: TrayLayout, before: number, geom: TrayGeometry): string {
  if (layout.lanes.length === 0) return `translate3d(${layout.offsetX}px, 0, 0)`;
  const at = layout.lanes[before];
  // The first lane of a row opens its gap on its left; any other lane opens
  // it after the lane before.
  if (at && (before === 0 || layout.lanes[before - 1]!.row !== at.row)) {
    return `translate3d(${Math.max(0, at.start - geom.laneGap)}px, ${at.y}px, 0)`;
  }
  const box = layout.lanes[Math.min(before, layout.lanes.length) - 1]!;
  return `translate3d(${box.end + geom.laneGap * 0.15}px, ${box.y}px, 0)`;
}

function validMeldSignatures(groups: readonly Group[], ctx: MeldContext): string[] {
  return groups
    .filter((group) => group.length >= ctx.config.minMeldSize && classifyMeld(group, ctx) !== null)
    .map((group) => group.join('|'));
}
